function handleImageLoad(img) {
	if (!img || img.classList.contains("is-loaded")) return;

	img.classList.add("is-loaded");

	const wrapper = img.closest(".product-item__image-wrapper");
	const placeholder = wrapper?.querySelector(
		".ed-placeholder-image-container"
	);

	if (img.dataset.srcset) {
		img.srcset = img.dataset.srcset;
		img.removeAttribute("data-srcset");
	}

	if (placeholder) {
		placeholder.classList.add("image-loaded");
		placeholder.style.opacity = "0";
		placeholder.style.pointerEvents = "none";
	}
}

const eventSystem = {
	events: {
		imageLoad: "ed:ajaxinate:loaded",
		filterRender: "ed:filters:rendered",
		wishlistUpdate: "wk:login",
		cartUnlock: "cart:unlock-state-changed",
	},

	handlers: {
		initializeProductList: () => {
			scheduleImagesInit();
		},

		handleCartUnlock: () => {
			const cartUnlockManager = document.querySelector(
				"cart-unlock-manager"
			);
			if (cartUnlockManager) cartUnlockManager.initWishlistButtons();
		},

		handleScroll: () => {
			// Placeholder scroll handler to prevent errors when eventSystem.init() adds scroll listener
			// kept to maintain eventSystem structure, if scroll-based functionality is needed later, we'll implement it here
		},
	},

	init() {
		const relevantEvents = ["imageLoad", "filterRender", "cartUnlock"];
		Object.entries(this.events).forEach(([key, eventName]) => {
			document.addEventListener(
				eventName,
				this.handlers.initializeProductList
			);
			if (relevantEvents.includes(key)) {
				document.addEventListener(
					eventName,
					this.handlers.handleCartUnlock
				);
			}
		});

		if (this.handlers.handleScroll) {
			window.addEventListener("scroll", this.handlers.handleScroll, {
				passive: true,
			});
		}
		document.addEventListener(
			"DOMContentLoaded",
			this.handlers.initializeProductList
		);
	},

	cleanup() {
		const relevantEvents = ["imageLoad", "filterRender", "cartUnlock"];
		Object.entries(this.events).forEach(([key, eventName]) => {
			document.removeEventListener(
				eventName,
				this.handlers.initializeProductList
			);
			if (relevantEvents.includes(key)) {
				document.removeEventListener(
					eventName,
					this.handlers.handleCartUnlock
				);
			}
		});
		if (this.handlers.handleScroll) {
			window.removeEventListener("scroll", this.handlers.handleScroll);
		}
	},
};

const isMobileMQ = () => window.matchMedia("(max-width: 740px)").matches;

const ioOptions = () => ({
	rootMargin: isMobileMQ() ? "2000px 0px" : "2000px 0px",
	threshold: 0.1,
});

function preloadImage(img, forcePreload = false) {
	if (img.complete && img.naturalWidth > 0) return Promise.resolve();

	if (!forcePreload && (img.src || img.srcset)) {
		return Promise.resolve();
	}

	return new Promise((resolve) => {
		let imageUrl = null;
		if (img.srcset) {
			imageUrl = img.srcset.split(",")[0].trim().split(" ")[0];
		} else if (img.dataset.srcset) {
			imageUrl = img.dataset.srcset.split(",")[0].trim().split(" ")[0];
		}

		if (!imageUrl) {
			resolve();
			return;
		}

		const preloadImg = new Image();
		preloadImg.onload = () => resolve();
		preloadImg.onerror = () => resolve();
		preloadImg.src = imageUrl;
	});
}

function isCarouselOwned(node) {
	return !!(node && node.closest("[data-carousel-owned]"));
}

const observerCallback = (entries) => {
	entries.forEach((entry) => {
		if (entry.isIntersecting) {
			const img = entry.target;

			if (isCarouselOwned(img)) return;

			if (img.getAttribute("loading") === "eager") return;

			if (img.getAttribute("loading") === "lazy") {
				img.setAttribute("loading", "eager");
			}

			if (img.dataset.srcset) {
				img.srcset = img.dataset.srcset;
				img.removeAttribute("data-srcset");
			}

			if (img.complete && img.naturalWidth > 0) {
				handleImageLoad(img);
				observer.unobserve(img);
			} else {
				if (!img.dataset._imgInit) {
					img.dataset._imgInit = "1";

					let checkInterval = null;
					let isUnobserved = false;
					const cleanup = () => {
						if (checkInterval) {
							clearInterval(checkInterval);
							checkInterval = null;
						}
					};

					const needsPreload =
						!img.src && !img.srcset && img.dataset.srcset;
					const shouldForcePreload =
						isMobileMQ() && img.srcset && !img.complete;

					if (needsPreload || shouldForcePreload) {
						preloadImage(img, shouldForcePreload).then(() => {
							if (img.complete && img.naturalWidth > 0) {
								cleanup();
								handleImageLoad(img);

								if (!isUnobserved) {
									observer.unobserve(img);
									isUnobserved = true;
								}
							}
						});
					}

					const onLoad = () => {
						cleanup();
						handleImageLoad(img);

						if (!isUnobserved) {
							observer.unobserve(img);
							isUnobserved = true;
						}
						if (
							isMobileMQ() &&
							img.classList.contains("product-item__mobile-image")
						) {
							const wrapper = img.closest(
								".product-item__image-wrapper"
							);
							if (wrapper) {
								setTimeout(
									() => applyMobileImageFallback(wrapper),
									100
								);
							}
						}
					};
					img.addEventListener("load", onLoad, { once: true });
					img.addEventListener("error", onLoad, { once: true });

					let checkCount = 0;
					const maxChecks = 8;
					checkInterval = setInterval(() => {
						checkCount++;
						if (img.complete && img.naturalWidth > 0) {
							cleanup();
							handleImageLoad(img);

							if (!isUnobserved) {
								observer.unobserve(img);
								isUnobserved = true;
							}
						} else if (checkCount >= maxChecks) {
							cleanup();

							if (!isUnobserved) {
								observer.unobserve(img);
								isUnobserved = true;
							}
						}
					}, 100);
				}
			}
		}
	});
};

let observer = null;
let observedImages = new WeakSet();

function rebuildObserver() {
	try {
		if (observer) {
			observer.disconnect();
		}
	} catch (e) {}
	observer = new IntersectionObserver(observerCallback, ioOptions());
	observedImages = new WeakSet();
}

rebuildObserver();

window.addEventListener(
	"resize",
	(() => {
		let t;
		let lastIsMobile = isMobileMQ();
		return () => {
			clearTimeout(t);
			t = setTimeout(() => {
				const nextIsMobile = isMobileMQ();
				if (nextIsMobile !== lastIsMobile) {
					lastIsMobile = nextIsMobile;
					rebuildObserver();
					scheduleImagesInit();
				}
			}, 200);
		};
	})(),
	{ passive: true }
);

let _imagesInitRAF = null;
let _isScrolling = false;
let _scrollTimeout = null;

function scheduleImagesInit(onlyViewport = false) {
	if (_imagesInitRAF) return;

	if (_isScrolling && onlyViewport === false) {
		onlyViewport = true;
	}

	_imagesInitRAF = requestAnimationFrame(() => {
		_imagesInitRAF = null;
		const productList = document.querySelector(".product-list__inner");
		if (productList) {
			if (onlyViewport) {
				initializeViewportImages(productList);
			} else {
				initializeImages(productList);
			}
		}
	});
}

window.addEventListener(
	"scroll",
	() => {
		_isScrolling = true;
		clearTimeout(_scrollTimeout);
		_scrollTimeout = setTimeout(() => {
			_isScrolling = false;
			scheduleImagesInit(false);
		}, 150);
	},
	{ passive: true }
);

function initializeViewportImages(container) {
	const viewportHeight = window.innerHeight;
	const buffer = isMobileMQ() ? 2000 : 1000;

	const images = container.querySelectorAll(
		"img.product-item__primary-image:not(.is-loaded):not([data-quick-buy-image]), " +
			"img.product-item__secondary-image:not(.is-loaded):not([data-quick-buy-image]), " +
			"img.product-item__mobile-image:not(.is-loaded):not([data-quick-buy-image])"
	);

	Array.from(images).forEach((img) => {
		if (isCarouselOwned(img)) return;

		const rect = img.getBoundingClientRect();
		const isInViewport =
			rect.top < viewportHeight + buffer && rect.bottom > -buffer;

		if (isInViewport) {
			if (img.complete && img.naturalWidth > 0) {
				handleImageLoad(img);
			} else {
				if (!img.dataset._imgInit) {
					img.dataset._imgInit = "1";
					if (img.dataset.srcset && !img.srcset) {
						img.srcset = img.dataset.srcset;
						img.removeAttribute("data-srcset");
					}
					if (img.getAttribute("loading") === "lazy") {
						img.setAttribute("loading", "eager");
					}
					if (!observedImages.has(img)) {
						observer.observe(img);
						observedImages.add(img);
					}
					img.addEventListener("load", () => handleImageLoad(img), {
						once: true,
					});
					img.addEventListener("error", () => handleImageLoad(img), {
						once: true,
					});
				}
			}
		}
	});
}

function initializePriorityImages() {
	const isDesktop = window.innerWidth > 740;
	if (!isDesktop) return;

	const productList = document.querySelector(".product-list__inner");
	if (!productList) return;

	const priorityImages = productList.querySelectorAll(
		'img[data-priority-load="true"]:not(.is-loaded):not([data-quick-buy-image])'
	);

	priorityImages.forEach((img) => {
		if (img.complete && img.naturalWidth > 0) {
			handleImageLoad(img);
		} else {
			if (!img.dataset._imgInit) {
				img.dataset._imgInit = "1";
				img.addEventListener("load", () => handleImageLoad(img), {
					once: true,
				});
				img.addEventListener("error", () => handleImageLoad(img), {
					once: true,
				});
			}
		}
	});
}

async function initializeImages(container) {
	try {
		if (!container) return;

		const images = container.querySelectorAll(
			"img.product-item__primary-image:not(.is-loaded):not([data-quick-buy-image]), " +
				"img.product-item__secondary-image:not(.is-loaded):not([data-quick-buy-image]), " +
				"img.product-item__mobile-image:not(.is-loaded):not([data-quick-buy-image])"
		);

		const imageArray = Array.from(images);

		imageArray.forEach((img) => {
			if (img.complete && img.naturalWidth > 0) {
				handleImageLoad(img);
			}
		});

		const remainingImages = imageArray.filter(
			(img) => !img.classList.contains("is-loaded")
		);

		const isDesktop = window.innerWidth > 740;

		remainingImages.forEach((img) => {
			if (isCarouselOwned(img)) return;

			if (!img.closest('.quick-buy-product:not([data-visible="true"])')) {
				const isPriorityLoad = img.dataset.priorityLoad === "true";
				const isMobile = !isDesktop;
				const isPrimaryImage = img.classList.contains(
					"product-item__primary-image"
				);
				const isEager = img.getAttribute("loading") === "eager";

				if (isEager) {
					if (img.dataset.srcset && !img.srcset) {
						img.srcset = img.dataset.srcset;
						img.removeAttribute("data-srcset");
					}
					if (!img.dataset._imgInit) {
						img.dataset._imgInit = "1";
						if (img.complete && img.naturalWidth > 0) {
							handleImageLoad(img);
						} else {
							preloadImage(img, true).then(() => {
								if (img.complete && img.naturalWidth > 0) {
									handleImageLoad(img);
								}
							});
							img.addEventListener(
								"load",
								() => handleImageLoad(img),
								{ once: true }
							);
							img.addEventListener(
								"error",
								() => handleImageLoad(img),
								{ once: true }
							);
						}
					}
					return;
				}

				if (isMobile && isPrimaryImage) {
					if (img.dataset.srcset && !img.srcset) {
						img.srcset = img.dataset.srcset;
						img.removeAttribute("data-srcset");
						if (img.getAttribute("loading") === "lazy") {
							img.removeAttribute("loading");
						}
					}
					if (!observedImages.has(img)) {
						observer.observe(img);
						observedImages.add(img);
					}
					if (!img.dataset._imgInit) {
						img.dataset._imgInit = "1";
						img.addEventListener(
							"load",
							() => handleImageLoad(img),
							{
								once: true,
							}
						);
						img.addEventListener(
							"error",
							() => handleImageLoad(img),
							{
								once: true,
							}
						);
					}
					return;
				}

				if (isDesktop && isPriorityLoad) {
					if (img.dataset.srcset) {
						img.srcset = img.dataset.srcset;
						img.removeAttribute("data-srcset");
					}

					if (!img.dataset._imgInit) {
						img.dataset._imgInit = "1";
						if (img.complete && img.naturalWidth > 0) {
							handleImageLoad(img);
						} else {
							preloadImage(img).then(() => {
								if (img.complete && img.naturalWidth > 0) {
									handleImageLoad(img);
								}
							});
							img.addEventListener(
								"load",
								() => handleImageLoad(img),
								{ once: true }
							);
							img.addEventListener(
								"error",
								() => handleImageLoad(img),
								{ once: true }
							);
						}
					}
				} else {
					if (!observedImages.has(img)) {
						observer.observe(img);
						observedImages.add(img);
					}

					if (!img.dataset._imgInit) {
						img.dataset._imgInit = "1";
						img.addEventListener(
							"load",
							() => handleImageLoad(img),
							{ once: true }
						);
						img.addEventListener(
							"error",
							() => handleImageLoad(img),
							{ once: true }
						);
					}
				}
			}
		});

		document.dispatchEvent(new Event("custom:images:initialized"));
	} catch (error) {
		console.error("Image initialization error:", error);
		imageArray?.forEach((img) => {
			if (img.dataset.srcset) {
				img.srcset = img.dataset.srcset;
				img.removeAttribute("data-srcset");
			}
			handleImageLoad(img);
		});
	}
}

function applyMobileImageFallback(root = document) {
	const cards = root.classList?.contains("product-item__image-wrapper")
		? [root]
		: root.querySelectorAll
			? root.querySelectorAll(".product-item__image-wrapper")
			: document.querySelectorAll(".product-item__image-wrapper");

	cards.forEach((wrapper) => {
		const carousel = wrapper.querySelector(
			".product-item__mobile-carousel"
		);
		const primary = wrapper.querySelector(".product-item__primary-image");

		if (!primary) return;

		const swiperBroken =
			!carousel ||
			carousel.querySelectorAll(".product-item__mobile-carousel-cell")
				.length === 0;

		let firstImageBroken = false;
		if (carousel && !swiperBroken) {
			const firstCell = carousel.querySelector(
				".product-item__mobile-carousel-cell"
			);
			if (firstCell) {
				const firstImg = firstCell.querySelector(
					"img.product-item__mobile-image"
				);
				if (
					!firstImg ||
					(firstImg.complete && firstImg.naturalWidth === 0) ||
					(!firstImg.complete &&
						!firstImg.srcset &&
						!firstImg.dataset.srcset)
				) {
					firstImageBroken = true;
				}
			} else {
				firstImageBroken = true;
			}
		}

		if ((swiperBroken || firstImageBroken) && primary) {
			wrapper.classList.add("show-primary-on-mobile");
			if (primary.dataset.srcset && !primary.srcset) {
				primary.srcset = primary.dataset.srcset;
				primary.removeAttribute("data-srcset");
				if (primary.getAttribute("loading") === "lazy") {
					primary.setAttribute("loading", "eager");
				}
			}
		} else {
			wrapper.classList.remove("show-primary-on-mobile");
		}
	});
}

eventSystem.init();

initializePriorityImages();

function preloadAllEagerImages() {
	const productList = document.querySelector(".product-list__inner");
	if (!productList) return;

	const eagerImages = productList.querySelectorAll(
		'img[loading="eager"]:not(.is-loaded):not([data-quick-buy-image])'
	);

	eagerImages.forEach((img) => {
		if (isCarouselOwned(img)) return;

		if (img.dataset.srcset && !img.srcset) {
			img.srcset = img.dataset.srcset;
			img.removeAttribute("data-srcset");
		}

		if (img.complete && img.naturalWidth > 0) {
			handleImageLoad(img);
			return;
		}

		if (img.srcset) {
			const imageUrl = img.srcset.split(",")[0].trim().split(" ")[0];
			if (imageUrl) {
				const preloadImg = new Image();
				preloadImg.src = imageUrl;

				preloadImg.onload = () => {
					if (img.complete && img.naturalWidth > 0) {
						handleImageLoad(img);
					} else {
						const checkComplete = setInterval(() => {
							if (img.complete && img.naturalWidth > 0) {
								clearInterval(checkComplete);
								handleImageLoad(img);
							}
						}, 50);
						setTimeout(() => clearInterval(checkComplete), 2000);
					}
				};
				preloadImg.onerror = () => {
					if (img.complete && img.naturalWidth > 0) {
						handleImageLoad(img);
					}
				};
			}
		} else if (img.src) {
			const preloadImg = new Image();
			preloadImg.src = img.src;
			preloadImg.onload = () => {
				if (img.complete && img.naturalWidth > 0) {
					handleImageLoad(img);
				}
			};
		}

		if (!img.dataset._imgInit) {
			img.dataset._imgInit = "1";
			img.addEventListener("load", () => handleImageLoad(img), {
				once: true,
			});
			img.addEventListener("error", () => handleImageLoad(img), {
				once: true,
			});
		}
	});
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", () => {
		initializePriorityImages();
		preloadAllEagerImages();
		requestAnimationFrame(() => scheduleImagesInit());
	});
} else {
	preloadAllEagerImages();
	requestAnimationFrame(() => scheduleImagesInit());
}

document.addEventListener("ed:filters:rendered", () => {
	preloadAllEagerImages();
	requestAnimationFrame(() => {
		applyMobileImageFallback();
		setTimeout(() => applyMobileImageFallback(), 500);
	});
});
document.addEventListener("ed:ajaxinate:loaded", (e) => {
	preloadAllEagerImages();
	const productList = document.querySelector(".product-list__inner");
	if (!productList) return;

	const allWrappers = productList.querySelectorAll(
		".product-item__image-wrapper"
	);
	const recentWrappers = Array.from(allWrappers).slice(-30);

	recentWrappers.forEach((wrapper) => {
		const images = wrapper.querySelectorAll(
			"img.product-item__primary-image:not(.is-loaded), " +
				"img.product-item__secondary-image:not(.is-loaded), " +
				"img.product-item__mobile-image:not(.is-loaded)"
		);

		images.forEach((img) => {
			if (isCarouselOwned(img)) return;

			if (!observedImages.has(img)) {
				if (img.dataset.srcset && !img.srcset) {
					img.srcset = img.dataset.srcset;
					img.removeAttribute("data-srcset");
				}

				if (img.getAttribute("loading") === "lazy") {
					const rect = img.getBoundingClientRect();
					const multiplier = isMobileMQ() ? 5 : 2;
					if (rect.top < window.innerHeight * multiplier) {
						img.setAttribute("loading", "eager");
					}
				}

				observer.observe(img);
				observedImages.add(img);
			}
		});
	});

	setTimeout(() => {
		applyMobileImageFallback();
		setTimeout(() => applyMobileImageFallback(), 500);
	}, 100);
});

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", () =>
		applyMobileImageFallback()
	);
} else {
	applyMobileImageFallback();
}

(function oneShotProductListKick() {
	const run = () => {
		initializePriorityImages();
		requestAnimationFrame(() => scheduleImagesInit());
	};
	const list = document.querySelector(".product-list__inner");
	if (list) return run();

	const mo = new MutationObserver((_m, obs) => {
		const l = document.querySelector(".product-list__inner");
		if (!l) return;
		obs.disconnect();
		run();
	});
	mo.observe(document.body, { childList: true, subtree: true });
})();

window.addEventListener("pageshow", (e) => {
	if (e.persisted || document.readyState === "complete") {
		rebuildObserver();

		document
			.querySelectorAll(
				"img.product-item__primary-image, img.product-item__secondary-image, img.product-item__mobile-image"
			)
			.forEach((img) => {
				if (isCarouselOwned(img)) return;

				const hasSrcset = !!img.getAttribute("srcset");
				const hasData = !!img.dataset.srcset;
				const loaded = img.classList.contains("is-loaded");

				if (!hasSrcset && hasData) {
					img.setAttribute("srcset", img.dataset.srcset);
					img.removeAttribute("data-srcset");
					if (
						img.getAttribute("loading") === "lazy" &&
						!img.complete
					) {
						img.setAttribute("loading", "eager");
					}
				}

				if (loaded && !hasSrcset && !hasData) {
					img.classList.remove("is-loaded");
				}

				if (!img.dataset._imgInit) {
					img.dataset._imgInit = "1";
					img.addEventListener("load", () => handleImageLoad(img), {
						once: true,
					});
					img.addEventListener("error", () => handleImageLoad(img), {
						once: true,
					});
				}

				if (!loaded || (!hasSrcset && hasData)) {
					try {
						observer.observe(img);
						observedImages.add(img);
					} catch (e) {}
				}
			});

		try {
			initializePriorityImages();
		} catch (e) {}
		requestAnimationFrame(() => {
			try {
				scheduleImagesInit();
			} catch (e) {}
			try {
				applyMobileImageFallback();
			} catch (e) {}
		});
	}
});
